import type { PrepaidPassState, PublicPrepaidPass } from './prepaid-pass-public.service';

/**
 * Fixed, customer-safe error copy for the public prepaid pass page (CWS-1835). Raw backend
 * messages are never rendered — only these strings reach the template.
 */
export const PREPAID_PASS_ERROR_INVALID = 'This prepaid link is invalid.';
export const PREPAID_PASS_ERROR_GENERIC = 'Something went wrong. Please try again later.';

const STATE_COPY: Record<PrepaidPassState, { headline: string; body: string }> = {
  active: {
    headline: 'Your wash QR code',
    body: 'Show this QR code at the wash to redeem. You can save it to your phone.',
  },
  inactive: {
    headline: 'Package not active',
    body: "This package can't be redeemed right now. Please contact the shop.",
  },
  expired: {
    headline: 'Package expired',
    body: 'The washes left on this package have expired.',
  },
  used: {
    headline: 'All washes used',
    body: 'Every wash on this package has been redeemed.',
  },
  cancelled: {
    headline: 'Package cancelled',
    body: 'This package has been cancelled.',
  },
};

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';

export interface PrepaidPassView {
  shopName: string | null;
  packageName: string | null;
  orderNumber: string | null;
  validTill: string | null;
  headline: string;
  body: string;
  /** Only a PNG data URL is ever bound to the page; anything else hides the QR. */
  qrCode: string | null;
  downloadFileName: string;
  items: { name: string; line: string }[];
}

/** "2 of 4 left", flagged when that wash type's expiry has passed. */
export function prepaidPassItemLine(item: { remaining: number; limit: number; expired: boolean }) {
  const line = `${item.remaining} of ${item.limit} left`;
  return item.expired ? `${line} (expired)` : line;
}

/** Safe file name for the saved QR, e.g. "halo-car-wash-OGI100-qr.png". */
export function prepaidPassQrFileName(shopName: string | null, orderNumber: string | null) {
  const slug = (value: string | null) =>
    String(value ?? '')
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '');
  const parts = [slug(shopName).toLowerCase() || 'prepaid-wash', slug(orderNumber)].filter(Boolean);
  return `${parts.join('-')}-qr.png`;
}

export function buildPrepaidPassView(pass: PublicPrepaidPass | null): PrepaidPassView | null {
  if (!pass) {
    return null;
  }
  const copy = STATE_COPY[pass.state] ?? STATE_COPY.used;
  const qrCode =
    pass.state === 'active' && pass.qrCode?.startsWith(PNG_DATA_URL_PREFIX) ? pass.qrCode : null;
  return {
    shopName: pass.shopName,
    packageName: pass.packageName,
    orderNumber: pass.orderNumber,
    validTill: pass.validTill,
    headline: copy.headline,
    body: copy.body,
    qrCode,
    downloadFileName: prepaidPassQrFileName(pass.shopName, pass.orderNumber),
    items: (pass.items ?? []).map(item => ({ name: item.name, line: prepaidPassItemLine(item) })),
  };
}
